import {
  MealTicketServing,
  type MealType,
} from '@seen/core/models/mealTicketServing'
import type { Participant } from '@seen/core/models/patients/participant'
import {
  useCreateIfNoneExistsMutation,
  useDeleteMutation,
  useSearch,
} from '@seen/react'
import { useCallback, useEffect, useMemo } from 'react'

// How often to re-pull the shared served state so other iPads' prints appear
// without a manual refresh. React Query also refetches on window focus.
const REFRESH_INTERVAL_MS = 20_000

export interface ServedParticipants {
  servedIds: Set<string>
  isLoading: boolean
  // Mark a participant served for the current meal (idempotent — repeat
  // prints of the same meal don't duplicate). Resolves once the shared
  // Procedure is written.
  markServed: (participant: Participant) => Promise<void>
  // Clear today's served state for every device and every meal.
  resetAll: () => Promise<void>
}

// Shared, cross-device "served today" state for the meal ticket grid, backed
// by `MealTicketServing` (a completed `Procedure`) instead of per-device
// localStorage. `servedIds` is scoped to `mealType`; Reset deletes every
// serving for the local day.
export function useServedParticipants(mealType: MealType): ServedParticipants {
  const servedQuery = useSearch(
    MealTicketServing,
    MealTicketServing.servedTodayQuery()
  )
  const { mutateAsync: createServing } =
    useCreateIfNoneExistsMutation<MealTicketServing>()
  const { mutateAsync: deleteServing } = useDeleteMutation(MealTicketServing)

  const { refetch } = servedQuery
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refetch()
      }
    }, REFRESH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [refetch])

  const servedIds = useMemo(
    () =>
      new Set(
        (servedQuery.data ?? [])
          .filter((serving) => serving.mealType === mealType)
          .map((serving) => serving.patientId)
          .filter((id): id is string => !!id)
      ),
    [servedQuery.data, mealType]
  )

  const markServed = useCallback(
    async (participant: Participant) => {
      // Writes follow the clock so a late UI timer tick cannot attach the
      // serving to the previous meal.
      const meal = MealTicketServing.currentMealType()
      await createServing({
        instance: new MealTicketServing({
          subject: participant.reference,
          mealType: meal,
        }),
        query: MealTicketServing.servedTodayForPatientQuery(
          participant.reference,
          meal
        ),
      })
    },
    [createServing]
  )

  const resetAll = useCallback(async () => {
    await Promise.all(
      (servedQuery.data ?? []).map((serving) => deleteServing(serving))
    )
  }, [servedQuery.data, deleteServing])

  return {
    servedIds,
    isLoading: servedQuery.isLoading,
    markServed,
    resetAll,
  }
}
